import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import BusinessEmployeeAdd from "./BusinessEmployeeAdd";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const BusinessEmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // read business details from sessionStorage (expected JSON string)
  let rawBiz = null;
  try {
    rawBiz = sessionStorage.getItem("businessDetails");
  } catch {
    rawBiz = null;
  }
  const businessDetails = rawBiz ? JSON.parse(rawBiz) : null;
  const businessId = businessDetails?.id ?? null;

  // fetch once (or when businessId changes)
  const fetchEmployees = async () => {
    if (!businessId) {
      setSnack({ open: true, message: "No business found in sessionStorage.", severity: "error" });
      return;
    }
    setLoading(true);
    try {
      // get all employees (or your API endpoint)
      const res = await ApiServices.get("external-employees/");
      const data = res?.data ?? res;
      const all = Array.isArray(data) ? data : [];
      // keep only those matching this business
      const filtered = all.filter((e) => {
        if (!e) return false;
        if (typeof e.business === "number") return Number(e.business) === Number(businessId);
        if (typeof e.business === "object" && e.business !== null) return Number(e.business.id) === Number(businessId);
        return false;
      });
      setEmployees(filtered);
    } catch (err) {
      console.error(err);
      let msg = "Failed to fetch employees.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // frontend-only filtering (derived)
  const displayedEmployees = useMemo(() => {
    if (!search) return employees;
    const q = search.trim().toLowerCase();
    return employees.filter((emp) => {
      if (!emp) return false;
      const fullName = `${emp.first_name ?? ""} ${emp.last_name ?? ""}`.toLowerCase();
      const email = (emp.email ?? "").toLowerCase();
      const phone = (emp.phone ?? "").toLowerCase();
      // you can expand fields to match on other properties
      return (
        fullName.includes(q) ||
        email.includes(q) ||
        phone.includes(q)
      );
    });
  }, [employees, search]);

  const openAddDialog = () => setOpenAdd(true);
  const closeAddDialog = () => setOpenAdd(false);

  const onAddSuccess = (msg = "Employee added") => {
    setSnack({ open: true, message: msg, severity: "success" });
    closeAddDialog();
    // re-fetch to get new data (or push to state if you prefer)
    fetchEmployees();
  };

  const handleDeleteClick = (emp) => {
    setDeleteTarget(emp);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setConfirmOpen(false);
    setLoading(true);
    try {
      await ApiServices.delete(`external-employees/${deleteTarget.id}/`);
      setSnack({ open: true, message: "Employee deleted", severity: "success" });
      await fetchEmployees();
    } catch (err) {
      console.error(err);
      let msg = "Delete failed.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  return (
    <Box sx={{ p: 3, width: "100%", boxSizing: "border-box", background: "#fff", minHeight: "60vh" }}>
      <style>{`
        .header-row { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
        .search-field { max-width:420px; width:100%; }
      `}</style>

      <Box className="header-row">
        <Box>
          <Typography variant="h5" sx={{ color: "#094c9f", fontWeight: 700 }}>
            Employees
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {businessDetails ? `${businessDetails.name}` : "Business not found in session storage"}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            className="search-field"
            size="small"
            placeholder="Search employees..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} sx={{ backgroundColor: "#094c9f" }}>
            Add Employee
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }} elevation={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "64vh", overflowX: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 700, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: "60px" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: "120px" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {displayedEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No employees found for this business.
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedEmployees.map((emp, idx) => (
                    <TableRow key={emp.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {emp.first_name} {emp.last_name}
                      </TableCell>
                      <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {emp.email}
                      </TableCell>
                      <TableCell>{emp.phone}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" aria-label="edit" disabled>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" aria-label="delete" onClick={() => handleDeleteClick(emp)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add dialog */}
      <Dialog open={openAdd} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Employee for {businessDetails?.name ?? ""}</DialogTitle>
        <DialogContent>
          <BusinessEmployeeAdd onSuccess={onAddSuccess} onCancel={closeAddDialog} />
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessEmployeeList;
